import React, { useState, useEffect } from 'react';
import { MdContentCopy, MdCheck, MdVpnKey } from 'react-icons/md';
import Modal from '../../ui/Modal';
import { Button, Notice } from '../../ui/PageShell';
import { Field, Input } from '../../ui/Form';
import './users.css';
import { codeText } from './accessCodeText';

/**
 * رمز الدخول — issue a standing login code for someone SMS can't reach.
 *
 * The one rule that shapes this whole dialog: the plain code exists for
 * exactly one moment, in the issue response. It is stored hashed, so
 * nobody — not the manager, not the database owner — can read it back
 * afterwards. If it's lost, you issue a new one.
 *
 * So the dialog makes that moment hard to miss: the code is shown big,
 * copyable, with an explicit warning that it won't be shown again.
 */

const fmt = (iso) => (iso ? String(iso).slice(0, 16).replace('T', ' ') : '—');

export default function UserAccessCodeModal({ user, existing: initial, onClose, onIssued, api, toast }) {
  // The list this dialog was opened from is a snapshot from page load,
  // and use_count/last_used_at move every time the person logs in — so
  // refetch on open and show the live numbers, not the stale ones.
  const [existing, setExisting] = useState(initial);
  useEffect(() => {
    let alive = true;
    api.accessCodes()
      .then((all) => {
        if (alive) setExisting((all || []).find((c) => String(c.user_id) === String(user.id)) || null);
      })
      .catch(() => { /* keep the snapshot we already have */ });
    return () => { alive = false; };
  }, [api, user.id]);

  const [note, setNote] = useState(initial?.note || '');
  const [issued, setIssued] = useState(null); // the one-time plain code
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmingReissue, setConfirmingReissue] = useState(false);

  const issue = async () => {
    setBusy(true);
    try {
      const r = await api.issueAccessCode(user.id, note.trim() || undefined);
      setIssued(r.code);
      setConfirmingReissue(false);
      toast.success('تم توليد رمز الدخول');
      onIssued && onIssued();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await api.revokeAccessCode(user.id);
      toast.success('تم إلغاء الرمز');
      onIssued && onIssued();
      onClose();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(issued);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error('تعذّر النسخ، انسخه يدوياً'); }
  };

  // Three states: just-issued (show the code), has an active code
  // (show its stats), or nothing yet (offer to create one).
  return (
    <Modal
      open
      size="md"
      title={`رمز الدخول — ${user.name}`}
      onClose={onClose}
      footer={
        issued ? (
          <Button variant="success" onClick={onClose}>تم، أغلق</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>إغلاق</Button>
            {existing && !confirmingReissue && (
              <Button variant="danger" onClick={revoke} loading={busy}>إلغاء الرمز</Button>
            )}
            {existing && !confirmingReissue && (
              <Button variant="secondary" onClick={() => setConfirmingReissue(true)}>توليد رمز جديد</Button>
            )}
            {(!existing || confirmingReissue) && (
              <Button onClick={issue} loading={busy}>
                {existing ? 'تأكيد التوليد' : 'توليد الرمز'}
              </Button>
            )}
          </>
        )
      }
    >
      {issued ? (
        <div className="user-code-issued">
          <Notice tone="warning" title="انسخ الرمز الآن">
            لن يظهر هذا الرمز مرة أخرى. إذا ضاع، ولّد رمزاً جديداً (وسيتوقف هذا عن العمل).
          </Notice>

          <div className="user-code-display">
            <MdVpnKey size={22} />
            <span className="user-code-value">{issued}</span>
            <button type="button" className="user-code-copy" onClick={copy} title="نسخ">
              {copied ? <MdCheck size={18} /> : <MdContentCopy size={18} />}
            </button>
          </div>

          <p className="user-code-help">
            يدخل «{user.name}» اسمه كالمعتاد، وعند طلب رمز التحقق يُدخل هذا الرمز
            بدل الرمز المرسل بالرسائل. الرمز صالح لعدد غير محدود من المرات حتى تُلغيه.
          </p>
        </div>
      ) : existing ? (
        <div className="user-code-existing">
          <Notice tone="info">
            لهذا الحساب رمز دخول فعّال. إن ضاع من صاحبه أو ظهر ناقصاً، ولّد رمزاً جديداً.
          </Notice>

          <dl className="user-code-stats">
            <div><dt>الرمز</dt><dd dir="ltr">{codeText(existing.code_hint)}</dd></div>
            <div><dt>عدد مرات الاستخدام</dt><dd>{existing.use_count}</dd></div>
            <div><dt>آخر استخدام</dt><dd>{fmt(existing.last_used_at)}</dd></div>
            <div><dt>أُصدر في</dt><dd>{fmt(existing.created_at)}</dd></div>
            <div><dt>أصدره</dt><dd>{existing.issued_by_name || '—'}</dd></div>
            {existing.note && <div><dt>ملاحظة</dt><dd>{existing.note}</dd></div>}
          </dl>

          {confirmingReissue && (
            <Notice tone="warning" title="توليد رمز جديد">
              سيتوقف الرمز الحالي عن العمل فوراً، وسيحتاج صاحبه للرمز الجديد.
            </Notice>
          )}
        </div>
      ) : (
        <div className="user-code-new">
          <p className="user-code-help">
            يُستخدم لمن لا تصله رسائل SMS. يُعطى له الرمز ليدخل به بدل رمز التحقق،
            ويبقى صالحاً لعدد غير محدود من المرات حتى تُلغيه.
          </p>
          <Field label="ملاحظة (اختياري)" hint="لماذا أُصدر هذا الرمز — يظهر لك لاحقاً فقط">
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="مثال: لا تصله الرسائل"
              maxLength={100}
            />
          </Field>
        </div>
      )}
    </Modal>
  );
}
